// src/pages/SharedLists.js
import React from 'react';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, deleteField, getDocs, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Check, X, UserPlus, ShieldBan } from 'lucide-react';
import IDX from '../utils/indices';
import { createSafeSearchClient } from '../utils/safeSearchClient';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../components/ConfirmModal';

// ---------- UI helpers ----------
function ActionBtn({ title, onClick, children, variant = 'default', isForest, t, compact = false }) {
  // Colored button variants
  const variants = {
    accept: {
      backgroundColor: '#22c55e',
      color: '#fff',
      border: 'none',
    },
    decline: {
      backgroundColor: '#ef4444',
      color: '#fff',
      border: 'none',
    },
    contact: {
      backgroundColor: '#4caf50',
      color: '#fff',
      border: 'none',
    },
    block: {
      backgroundColor: '#f97316',
      color: '#fff',
      border: 'none',
    },
    default: {
      backgroundColor: isForest ? 'rgba(51,65,85,0.7)' : t?.surface?.elevated || '#f8fafc',
      border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t?.surface?.cardBorder || '#c8e6c9'}`,
      color: isForest ? '#e8f5e9' : t?.text?.primary || '#1B5E20',
    },
  };
  const btnStyle = variants[variant] || variants.default;
  const classes = compact
    ? "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:opacity-90 transition-opacity"
    : "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium hover:opacity-90 transition-opacity";
  return (
    <button title={title} onClick={onClick} className={classes} style={btnStyle}>
      {children}
    </button>
  );
}

function BlockedUsersModal({ open, onClose, uid, isForest, t, tr }) {
  const [map, setMap] = React.useState({});
  React.useEffect(() => {
    if (!open || !uid) return;
    (async () => {
      try { const d = await getDoc(doc(db, 'blockedUsers', uid)); setMap(d.exists() ? d.data() || {} : {}); }
      catch { setMap({}); }
    })();
  }, [open, uid]);

  const handleUnblock = async (phone) => {
    try { await updateDoc(doc(db, 'blockedUsers', uid), { [phone]: deleteField() }); setMap((m) => { const n = { ...m }; delete n[phone]; return n; }); }
    catch (e) { console.error(e); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div
        className="w-[520px] max-w-[95vw] rounded-xl p-4"
        style={{
          backgroundColor: isForest ? 'rgba(15,58,48,0.95)' : '#ffffff',
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
          color: isForest ? '#e8f5e9' : t.text.primary,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{tr('sharedLists.blockedUsers')}</h2>
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: isForest ? 'rgba(51,65,85,0.7)' : t.surface.elevated,
              border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
              color: isForest ? '#e8f5e9' : t.text.primary,
            }}
          >
            {tr('common.close')}
          </button>
        </div>
        <div className="space-y-2 max-h-[60vh] overflow-auto">
          {Object.keys(map).length === 0 ? (
            <div style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.noBlockedUsers')}</div>
          ) : (
            Object.entries(map).map(([phone, val]) => (
              <div
                key={phone}
                className="flex items-center justify-between rounded-lg p-2"
                style={{
                  backgroundColor: isForest ? 'rgba(51,65,85,0.6)' : t.surface.elevated,
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
                }}
              >
                <div>
                  <div className="font-medium">{phone}</div>
                </div>
                <button
                  className="rounded-md px-2 py-1 text-sm hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: isForest ? 'rgba(71,85,105,0.7)' : t.surface.card,
                    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
                    color: isForest ? '#e8f5e9' : t.text.primary,
                  }}
                  onClick={() => handleUnblock(phone)}
                >
                  {tr('sharedLists.unblock')}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DeletedItemsModal({ open, onClose, deletedItems, isForest, t, tr }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div
        className="w-[520px] max-w-[95vw] rounded-xl p-4"
        style={{
          backgroundColor: isForest ? 'rgba(27,46,32,0.98)' : '#ffffff',
          border: `1px solid ${isForest ? 'rgba(129,199,132,0.3)' : t.surface.cardBorder}`,
          color: isForest ? '#e8f5e9' : t.text.primary,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{tr('sharedLists.deletedItemsTitle')}</h2>
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: isForest ? 'rgba(56,142,60,0.3)' : t.surface.elevated,
              border: `1px solid ${isForest ? 'rgba(129,199,132,0.3)' : t.surface.cardBorder}`,
              color: isForest ? '#a5d6a7' : t.text.primary,
            }}
          >
            {tr('common.close')}
          </button>
        </div>
        <div className="mb-4 text-sm" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
          {tr('sharedLists.deletedItemsExplanation')}
        </div>
        <div className="space-y-2 max-h-[40vh] overflow-auto">
          {deletedItems.length === 0 ? (
            <div style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.noDeletedItems')}</div>
          ) : (
            deletedItems.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between rounded-lg p-2"
                style={{
                  backgroundColor: isForest ? 'rgba(56,142,60,0.2)' : t.surface.elevated,
                  border: `1px solid ${isForest ? 'rgba(129,199,132,0.2)' : t.surface.cardBorder}`,
                }}
              >
                <div>
                  <div className="font-medium text-sm">{tr('sharedLists.unknownItems')}</div>
                  <div className="text-xs" style={{ color: isForest ? '#81c784' : t.text.tertiary }}>
                    ID: {item.objectID}
                  </div>
                </div>
                <div className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                  {tr('sharedLists.itemsDeleted')}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mt-4 pt-3 text-xs" style={{ borderTop: `1px solid ${isForest ? 'rgba(129,199,132,0.2)' : t.surface.cardBorder}`, color: isForest ? '#81c784' : t.text.tertiary }}>
          {tr('sharedLists.excludedFromImport')}
        </div>
      </div>
    </div>
  );
}


function materialToIndex(materialRaw) {
  const m = String(materialRaw || '').toUpperCase();
  if (m.includes('METAL')) return IDX.metals;
  if (m.includes('TRUCK')) return IDX.trucks;
  return IDX.converters;
}

// Prefer v5 getObjects / multipleGetObjects; fallback to per-index getObjects if needed.
async function safeMultiGet(groups, client) {
  const requests = [];
  Object.keys(groups).forEach((indexName) => {
    const ids = groups[indexName] || [];
    ids.forEach((objectID) => requests.push({ indexName, objectID }));
  });
  if (!requests.length) return [];

  try {
    if (client && typeof client.getObjects === 'function') {
      const { results } = await client.getObjects({ requests });
      return results || [];
    }
  } catch (e) { console.error('[SL] Algolia getObjects error', e); }

  try {
    if (client && typeof client.multipleGetObjects === 'function') {
      const { results } = await client.multipleGetObjects({ requests });
      return results || [];
    }
  } catch (e) { console.error('[SL] Algolia multipleGetObjects error', e); }

  try {
    const all = [];
    for (const indexName of Object.keys(groups)) {
      const ids = groups[indexName];
      if (!ids.length) continue;
      const getIndex = (typeof client.searchIndex === 'function')
        ? () => client.searchIndex(indexName)
        : (typeof client.initIndex === 'function')
          ? () => client.initIndex(indexName)
          : null;
      if (!getIndex) continue;
      const index = getIndex();
      if (!index || typeof index.getObjects !== 'function') continue;
      // Try v5 signature first, then v4
      let res = null;
      try {
        res = await index.getObjects({ objectIDs: ids });
      } catch {
        res = await index.getObjects(ids);
      }
      if (res?.results) all.push(...res.results.filter(Boolean));
    }
    return all;
  } catch (e) { console.error('[SL] per-index getObjects fallback error', e); return []; }
}

// ---------- Page ----------
export default function SharedLists() {
  // ---------- Algolia helpers ----------
  const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
  const apiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

  const { ready, uid } = useUser();
  const { theme: t, isForest } = useTheme();
  const { t: tr } = useTranslation();
  const searchClient = React.useMemo(() => createSafeSearchClient(appId, apiKey), [appId, apiKey]);
  const [meta, setMeta] = React.useState({}); // listId -> { sender, userID, title, date }
  const [selectedId, setSelectedId] = React.useState('');
  const [items, setItems] = React.useState([]); // preview items hydrated from Algolia
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [blockedOpen, setBlockedOpen] = React.useState(false);
  const [deletedOpen, setDeletedOpen] = React.useState(false);
  const [contacts, setContacts] = React.useState({}); // phone -> {name, phone}
  const [busyId, setBusyId] = React.useState('');
  const [toast, setToast] = React.useState({ type: '', msg: '' });
  const [pendingDeclineId, setPendingDeclineId] = React.useState(null);
  const [previewPage, setPreviewPage] = React.useState(0);
  const [lastViewedTime, setLastViewedTime] = React.useState(null); // For highlighting new items
  const PREVIEW_PAGE_SIZE = 6;
  const DESKTOP_PAGE_SIZE = 12;

  // Check if a list is "new" (received after lastViewedTime)
  const isNew = React.useCallback((listDate) => {
    if (!lastViewedTime || !listDate) return false;
    const listTime = typeof listDate === 'number' ? listDate : (listDate.seconds ? listDate.seconds * 1000 : new Date(listDate).getTime());
    return listTime > lastViewedTime;
  }, [lastViewedTime]);

  // Compute deleted items (items with isDeleted flag) and valid items
  const deletedItems = React.useMemo(() => items.filter(it => it.isDeleted), [items]);
  const deletedCount = deletedItems.length;
  const validItems = React.useMemo(() => items.filter(it => !it.isDeleted), [items]);

  React.useEffect(() => {
    console.debug('[SL] ALGOLIA APP_ID present:', !!appId, 'SEARCH_KEY length:', (apiKey || '').length);
  }, [appId, apiKey]);

  React.useEffect(() => {
    if (!uid) return;
    (async () => {
      try { const c = await getDoc(doc(db, 'userContacts', uid)); setContacts(c.exists() ? c.data() || {} : {}); }
      catch { setContacts({}); }
    })();
  }, [uid]);

  React.useEffect(() => {
    if (!ready || !uid) return;
    (async () => {
      setLoading(true); setError('');
      try { const snap = await getDoc(doc(db, 'sharedLists', uid)); const data = snap.exists() ? (snap.data() || {}) : {}; setMeta(data); }
      catch (e) { setError(e.message || String(e)); }
      finally { setLoading(false); }
    })();
  }, [ready, uid]);

  // Mark shared lists as viewed immediately when page loads
  React.useEffect(() => {
    if (!ready || !uid) return;
    (async () => {
      try {
        // Fetch lastViewedSharedListsAt BEFORE updating - for highlighting new items
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const lvs = userDoc.data()?.lastViewedSharedListsAt;
          if (lvs) {
            const time = lvs.toDate ? lvs.toDate().getTime() : (lvs.seconds ? lvs.seconds * 1000 : lvs);
            setLastViewedTime(time);
          }
        }
      } catch { }
      // Immediately clear the badge in local state (no Firestore wait)
      window.dispatchEvent(new Event('clearSharedBadge'));
      // Then update Firestore in background
      try {
        await setDoc(doc(db, 'users', uid), { lastViewedSharedListsAt: serverTimestamp() }, { merge: true });
        // Don't dispatch refreshBadgeCounts here - it causes a race condition
      } catch (err) {
        console.error('[SharedLists] Failed to update lastViewedSharedListsAt:', err);
      }
    })();
  }, [ready, uid]);

  const loadItems = React.useCallback(async (listId) => {
    const info = meta?.[listId]; if (!info || !info.userID) { setItems([]); return; }
    try {
      const d = await getDoc(doc(db, 'sharedLists', uid, info.userID, listId));
      const data = d.exists() ? d.data() : {};
      // Extract all relevant fields including code and brand from shared data
      const raw = Object.values(data.list || data).map((v) => ({
        objectID: v.objectID || v.objectId || '',
        material: v.material || v?.data?.material || 'CERAMIC',
        total: Number(v.total) || 1,
        // Include code and brand if present in shared data (new lists)
        code: v.code || v?.data?.code || '',
        brand: v.brand || v?.data?.brand || '',
      })).filter((x) => x.objectID);
      if (raw.length === 0) { setItems([]); return; }

      // For legacy lists (missing code/brand), use Algolia to look up details
      const needsAlgoliaLookup = raw.some((r) => !r.code && !r.brand);

      let byId = Object.create(null);
      if (needsAlgoliaLookup) {
        const groups = { [IDX.converters]: [], [IDX.metals]: [], [IDX.trucks]: [] };
        raw.forEach((r) => { groups[materialToIndex(r.material)].push(r.objectID); });
        const hits = await safeMultiGet(groups, searchClient);
        hits.forEach((h) => { if (h && h.objectID) byId[h.objectID] = h; });
      }

      const arr = raw.map((r) => {
        const h = byId[r.objectID] || {};
        const img = Array.isArray(h.imgURL) ? h.imgURL[0] : (h.imgURL || '');
        // Get code and brand from all possible sources
        const finalCode = r.code || h.code || h?.data?.code || '';
        const finalBrand = r.brand || h.brand || h?.data?.brand || '';
        // Mark items as deleted only if no code AND no brand from any source
        return {
          brand: finalBrand,
          code: finalCode,
          img,
          objectID: r.objectID,
          total: r.total,
          material: r.material,
          isDeleted: !finalCode && !finalBrand,
        };
      });

      setItems(arr);
    } catch (e) { console.error('[SharedLists] loadItems error:', e); setItems([]); }
  }, [meta, uid, searchClient]);

  React.useEffect(() => {
    if (selectedId) {
      loadItems(selectedId);
      setPreviewPage(0); // Reset pagination when selecting new list
    }
  }, [selectedId, loadItems]);

  // Functions instance
  const functions = React.useMemo(() => getFunctions(getApp(), 'us-central1'), []);

  const makeListKey = (name) => String(name || '').replace(/\s+/g, '').toLowerCase();

  async function handleAccept(listId) {
    try {
      const m = meta[listId]; if (!m) return;
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      const listsMap = userData?.lists || {};

      // Ask for a name, default to sender's provided title
      let desiredName = window.prompt(tr('sharedLists.nameThisList'), m?.title || `shared-${listId.slice(0, 6)}`);
      if (!desiredName) return;
      let key = makeListKey(desiredName);
      while (listsMap[key]) {
        const rename = window.prompt(tr('sharedLists.nameExistsMessage'), desiredName + ' (copy)');
        if (!rename) return;
        desiredName = rename;
        key = makeListKey(desiredName);
      }

      const importFn = httpsCallable(functions, 'importUserBuyLists2', { timeout: 1080000 });
      setBusyId(listId);
      await importFn({ uid, listName: desiredName, senderUid: m.userID, listId });

      // Refresh local metadata
      const snap = await getDoc(doc(db, 'sharedLists', uid));
      const data = snap.exists() ? (snap.data() || {}) : {};
      setMeta(data);
      setSelectedId(null);
      setItems([]);
      setToast({ type: 'success', msg: tr('sharedLists.listImportedSuccess') });
      setTimeout(() => setToast({ type: '', msg: '' }), 2500);
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', msg: e?.message || tr('sharedLists.failedToImport') });
      setTimeout(() => setToast({ type: '', msg: '' }), 3500);
    } finally { setBusyId(''); }
  }

  async function handleDecline(listId, opts = { keepMeta: false }) {
    try {
      const m = meta[listId]; if (!m) return;
      await deleteDoc(doc(db, 'sharedLists', uid, m.userID, listId));
      if (!opts.keepMeta) { const next = { ...meta }; delete next[listId]; setMeta(next); await updateDoc(doc(db, 'sharedLists', uid), { [listId]: deleteField() }); }
    } catch (e) { console.error(e); }
  }

  async function handleAddOrRenameContact(listId) {
    try {
      const m = meta[listId]; if (!m) return;
      const phone = String(m.sender || '').trim();
      if (!phone) return;
      const existing = contacts[phone]?.name;
      // Pre-fill with sender's display name if available and no existing contact name
      const suggestedName = existing || m.senderName || '';
      const name = window.prompt(existing ? tr('sharedLists.renameContact') + ':' : tr('sharedLists.addContact') + ':', suggestedName);
      if (!name) return;
      await setDoc(doc(db, 'userContacts', uid), { [phone]: { name, phone } }, { merge: true });
      setContacts((c) => ({ ...c, [phone]: { name, phone } }));
    } catch (e) { console.error(e); }
  }

  async function handleBlockSender(listId) {
    try {
      const m = meta[listId]; if (!m) return;
      const phone = String(m.sender || '').trim();
      if (!phone) return;
      await setDoc(doc(db, 'blockedUsers', uid), { [phone]: true }, { merge: true });
      // Remove all lists from this sender
      const parent = collection(db, 'sharedLists', uid, m.userID);
      const all = await getDocs(parent);
      const next = { ...meta };
      await Promise.all(all.docs.map(async (docSnap) => {
        await deleteDoc(docSnap.ref);
        delete next[docSnap.id];
        await updateDoc(doc(db, 'sharedLists', uid), { [docSnap.id]: deleteField() });
      }));
      setMeta(next);
    } catch (e) { console.error(e); }
  }

  const cardStyle = {
    backgroundColor: isForest ? 'rgba(26,77,64,0.6)' : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
  };

  const itemCardStyle = {
    backgroundColor: isForest ? 'rgba(15,23,42,0.5)' : t.surface.elevated,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
  };

  if (!ready) return <div className="p-6" style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.loading')}</div>;
  if (!uid) return <div className="p-6" style={{ color: t.status?.error || '#ef4444' }}>{tr('sharedLists.signInRequired')}</div>;

  // Sort by date (newest first)
  const entries = Object.entries(meta)
    .map(([id, m]) => ({ id, ...m }))
    .sort((a, b) => {
      const dateA = typeof a.date === 'number' ? a.date : (a.date?.seconds ? a.date.seconds * 1000 : 0);
      const dateB = typeof b.date === 'number' ? b.date : (b.date?.seconds ? b.date.seconds * 1000 : 0);
      return dateB - dateA; // newest first
    });

  return (
    <div className="px-0 md:px-0 pt-3" style={{ color: isForest ? '#e8f5e9' : t.text.primary }}>
      <div className="flex items-center justify-end mb-3">
        <button
          className="rounded-lg px-3 py-2 text-sm hover:opacity-80"
          style={cardStyle}
          onClick={() => setBlockedOpen(true)}
        >{tr('sharedLists.blockedUsers')}</button>
      </div>

      {toast.msg && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-sm ${toast.type === 'success' ? 'bg-green-900/40 border border-green-600 text-green-200' : 'bg-red-900/40 border border-red-600 text-red-200'}`}>
          {toast.msg}
        </div>
      )}

      {/* Mobile: Stack vertically */}
      <div className="block md:hidden">
        {loading ? (
          <div className="p-4 rounded-xl" style={cardStyle}>
            <span style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.loading')}</span>
          </div>
        ) : error ? (
          <div className="p-4 rounded-xl" style={cardStyle}>
            <span style={{ color: t.status?.error || '#ef4444' }}>{error}</span>
          </div>
        ) : entries.length === 0 ? (
          <div className="p-4 rounded-xl" style={cardStyle}>
            <span style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{tr('sharedLists.noSharedLists')}</span>
          </div>
        ) : (
          entries.map((e, idx) => {
            const dividerColor = isForest ? 'rgba(100,116,139,0.4)' : '#4caf50';
            const senderContact = String(e.sender || '').trim();
            const contactName = contacts[senderContact]?.name || null;
            // Display name priority: saved contact name > sender's profile name > just contact info
            const displayName = contactName || e.senderName || null;
            let ts = '-';
            if (e.date) { const dd = new Date(e.date); ts = isNaN(dd.getTime()) ? String(e.date) : dd.toLocaleString(); }
            const active = selectedId === e.id;
            return (
              <React.Fragment key={e.id}>
                {idx > 0 && (
                  <div className="my-3" style={{ height: 2, backgroundColor: dividerColor }} />
                )}
                <div
                  className="rounded-xl p-4"
                  style={{ ...cardStyle, backgroundColor: active ? (isForest ? 'rgba(51,65,85,0.6)' : t.navigation?.itemBackgroundActive || t.surface.elevated) : cardStyle.backgroundColor }}
                >
                  <div className="flex flex-col gap-3">
                    <div className="min-w-0" onClick={() => setSelectedId(active ? '' : e.id)} style={{ cursor: 'pointer' }}>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-lg truncate">{e.title || '-'}</div>
                        {isNew(e.date) && (
                          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: '#2e7d32', color: '#fff' }}>
                            {tr('common.new') || 'NEW'}
                          </span>
                        )}
                      </div>
                      {/* Show sender name if available, with phone/email below */}
                      {displayName ? (
                        <>
                          <div className="text-sm font-medium truncate" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{tr('sharedLists.from')}: {displayName}</div>
                          <div className="text-xs truncate" style={{ color: isForest ? '#81c784' : t.text.tertiary }}>{senderContact}</div>
                        </>
                      ) : (
                        <div className="text-sm truncate" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{tr('sharedLists.from')}: {senderContact || '-'}</div>
                      )}
                      <div className="text-xs mt-1" style={{ color: isForest ? '#81c784' : t.text.tertiary }}>{ts}</div>
                    </div>
                    <div className="flex flex-nowrap items-center gap-1">
                      <ActionBtn isForest={isForest} t={t} variant="accept" title={tr('sharedLists.accept')} compact onClick={(ev) => { ev.stopPropagation(); if (!busyId) handleAccept(e.id); }}>
                        {busyId === e.id ? (<><Check size={14} /></>) : (<><Check size={14} />{tr('sharedLists.accept')}</>)}
                      </ActionBtn>
                      <ActionBtn isForest={isForest} t={t} variant="decline" title={tr('sharedLists.decline')} compact onClick={(ev) => { ev.stopPropagation(); setPendingDeclineId(e.id); }}><X size={14} />{tr('sharedLists.decline')}</ActionBtn>
                      <ActionBtn isForest={isForest} t={t} variant="contact" title={contactName ? tr('sharedLists.renameContact') : tr('sharedLists.addContact')} compact onClick={(ev) => { ev.stopPropagation(); handleAddOrRenameContact(e.id); }}><UserPlus size={14} />{contactName ? tr('sharedLists.rename') : tr('sharedLists.contact')}</ActionBtn>
                      <ActionBtn isForest={isForest} t={t} variant="block" title={tr('sharedLists.blockSender')} compact onClick={(ev) => { ev.stopPropagation(); handleBlockSender(e.id); }}><ShieldBan size={14} />{tr('sharedLists.block')}</ActionBtn>
                    </div>
                    {active && items.length > 0 && (
                      <div className="mt-2 pt-3" style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }}>
                        <div className="flex items-center justify-between mb-2 text-sm" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
                          <div className="flex items-center gap-2">
                            <span>{tr('sharedLists.preview')}</span>
                            {deletedCount > 0 && (
                              <button
                                onClick={(ev) => { ev.stopPropagation(); setDeletedOpen(true); }}
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: '#ef4444', color: '#fff' }}
                              >
                                {tr('sharedLists.deletedItems')} ({deletedCount})
                              </button>
                            )}
                          </div>
                          <span className="font-medium">{tr('buyingLists.quantity')}: {validItems.reduce((s, it) => s + (it.total || 1), 0)}</span>
                        </div>
                        <div className="space-y-2">
                          {validItems.slice(previewPage * PREVIEW_PAGE_SIZE, (previewPage + 1) * PREVIEW_PAGE_SIZE).map((it, i) => (
                            <div key={i} className="rounded-lg p-2 flex gap-2" style={itemCardStyle}>
                              {it.img ? (<img src={it.img} alt={it.code || ''} className="h-10 w-10 object-cover rounded" />) : null}
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate">{it.code || '-'}</div>
                                <div className="text-xs flex items-center justify-between gap-2" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
                                  <span className="truncate">{it.brand || '-'}</span>
                                  <span className="shrink-0 font-semibold" style={{ color: isForest ? '#4caf50' : t.interactive.primary }}>Qty: {it.total || 0}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Pagination buttons */}
                          {items.length > PREVIEW_PAGE_SIZE && (
                            <div className="flex items-center justify-center gap-2 pt-2">
                              <button
                                onClick={(ev) => { ev.stopPropagation(); setPreviewPage(p => Math.max(0, p - 1)); }}
                                disabled={previewPage === 0}
                                className="px-3 py-1.5 rounded-md text-sm font-medium transition-opacity disabled:opacity-40"
                                style={{
                                  backgroundColor: t.interactive.primary,
                                  color: '#ffffff',
                                }}
                              >
                                Prev
                              </button>
                              <span className="text-xs px-2" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
                                {previewPage + 1} / {Math.ceil(items.length / PREVIEW_PAGE_SIZE)}
                              </span>
                              <button
                                onClick={(ev) => { ev.stopPropagation(); setPreviewPage(p => Math.min(Math.ceil(items.length / PREVIEW_PAGE_SIZE) - 1, p + 1)); }}
                                disabled={previewPage >= Math.ceil(items.length / PREVIEW_PAGE_SIZE) - 1}
                                className="px-3 py-1.5 rounded-md text-sm font-medium transition-opacity disabled:opacity-40"
                                style={{
                                  backgroundColor: t.interactive.primary,
                                  color: '#ffffff',
                                }}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Desktop: Side by side - only show preview panel if a list is selected */}
      <div className="hidden md:grid gap-4 md:grid-cols-12">
        {/* Left: available shared lists - always same width as when preview is shown */}
        <div className="rounded-xl p-4 md:col-span-5" style={cardStyle}>
          <h3 className="text-lg font-semibold mb-3">{tr('sharedLists.title')}</h3>
          {loading ? (
            <div className="p-4" style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.loading')}</div>
          ) : error ? (
            <div className="p-4" style={{ color: t.status?.error || '#ef4444' }}>{error}</div>
          ) : entries.length === 0 ? (
            <div className="p-4" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{tr('sharedLists.noSharedLists')}</div>
          ) : (
            <div>
              {entries.map((e, idx) => {
                const phone = String(e.sender || '').trim();
                const contactName = contacts[phone]?.name || null;
                const fromDisplay = contactName || phone || '-';
                let ts = '-';
                if (e.date) { const dd = new Date(e.date); ts = isNaN(dd.getTime()) ? String(e.date) : dd.toLocaleString(); }
                const active = selectedId === e.id;
                const dividerColor = isForest ? 'rgba(100,116,139,0.4)' : '#4caf50';
                return (
                  <React.Fragment key={e.id}>
                    {idx > 0 && (
                      <div className="my-2" style={{ height: 1, backgroundColor: dividerColor }} />
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      className="w-full text-left p-3 rounded-lg cursor-pointer transition-colors"
                      style={{
                        backgroundColor: active ? (isForest ? 'rgba(51,65,85,0.6)' : t.navigation?.itemBackgroundActive || t.surface.elevated) : 'transparent',
                      }}
                      onClick={() => setSelectedId(e.id)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') setSelectedId(e.id); }}
                    >
                      <div className="flex flex-col gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold truncate">{e.title || '-'}</div>
                            {isNew(e.date) && (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: '#2e7d32', color: '#fff' }}>
                                {tr('common.new') || 'NEW'}
                              </span>
                            )}
                          </div>
                          <div className="text-sm truncate" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{tr('sharedLists.from')}: {fromDisplay}</div>
                          <div className="text-xs" style={{ color: isForest ? '#81c784' : t.text.tertiary }}>{ts}</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <ActionBtn isForest={isForest} t={t} variant="accept" title={tr('sharedLists.accept')} onClick={(ev) => { ev.stopPropagation(); if (!busyId) handleAccept(e.id); }}>
                            {busyId === e.id ? (<><Check size={16} />{tr('sharedLists.importing')}</>) : (<><Check size={16} />{tr('sharedLists.accept')}</>)}
                          </ActionBtn>
                          <ActionBtn isForest={isForest} t={t} variant="decline" title={tr('sharedLists.decline')} onClick={(ev) => { ev.stopPropagation(); setPendingDeclineId(e.id); }}><X size={16} />{tr('sharedLists.decline')}</ActionBtn>
                          <ActionBtn isForest={isForest} t={t} variant="contact" title={contactName ? tr('sharedLists.renameContact') : tr('sharedLists.addContact')} onClick={(ev) => { ev.stopPropagation(); handleAddOrRenameContact(e.id); }}><UserPlus size={16} />{contactName ? tr('sharedLists.rename') : tr('sharedLists.contact')}</ActionBtn>
                          <ActionBtn isForest={isForest} t={t} variant="block" title={tr('sharedLists.blockSender')} onClick={(ev) => { ev.stopPropagation(); handleBlockSender(e.id); }}><ShieldBan size={16} />{tr('sharedLists.block')}</ActionBtn>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: compact preview of selected list - only show when a list is selected */}
        {selectedId && (
          <div className="md:col-span-7 rounded-xl p-4" style={cardStyle}>
            <h3 className="text-lg font-semibold mb-3">{tr('sharedLists.preview')}</h3>
            {items.length === 0 ? (
              <div className="p-6" style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>{tr('sharedLists.noItemsOrFailed')}</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3 text-sm" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
                  <div className="flex items-center gap-2">
                    <span>{validItems.length} {tr('buyingLists.items')}</span>
                    {deletedCount > 0 && (
                      <button
                        onClick={() => setDeletedOpen(true)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: '#ef4444', color: '#fff' }}
                      >
                        {tr('sharedLists.deletedItems')} ({deletedCount})
                      </button>
                    )}
                  </div>
                  <div className="font-medium">{tr('buyingLists.totalQuantity')}: {validItems.reduce((s, it) => s + (it.total || 1), 0)}</div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                  {validItems.slice(previewPage * DESKTOP_PAGE_SIZE, (previewPage + 1) * DESKTOP_PAGE_SIZE).map((it, i) => (
                    <div key={i} className="rounded-lg p-3 flex gap-3" style={itemCardStyle}>
                      {it.img ? (<img src={it.img} alt={it.code || ''} className="h-14 w-14 object-cover rounded" />) : null}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{it.code || '-'}</div>
                        <div className="text-sm truncate" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>{it.brand || '-'}</div>
                        <div className="mt-1 text-sm font-semibold" style={{ color: isForest ? '#4caf50' : '#2e7d32' }}>
                          {tr('buyingLists.quantity')}: {it.total || 0}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop pagination buttons */}
                {validItems.length > DESKTOP_PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-3 pt-4">
                    <button
                      onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                      disabled={previewPage === 0}
                      className="px-4 py-2 rounded-md text-sm font-medium transition-opacity disabled:opacity-40"
                      style={{
                        backgroundColor: t.interactive.primary,
                        color: '#ffffff',
                      }}
                    >
                      {tr('common.prev')}
                    </button>
                    <span className="text-sm" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
                      {tr('buyingLists.pageXofY', { current: previewPage + 1, total: Math.ceil(validItems.length / DESKTOP_PAGE_SIZE) })}
                    </span>
                    <button
                      onClick={() => setPreviewPage(p => Math.min(Math.ceil(validItems.length / DESKTOP_PAGE_SIZE) - 1, p + 1))}
                      disabled={previewPage >= Math.ceil(validItems.length / DESKTOP_PAGE_SIZE) - 1}
                      className="px-4 py-2 rounded-md text-sm font-medium transition-opacity disabled:opacity-40"
                      style={{
                        backgroundColor: t.interactive.primary,
                        color: '#ffffff',
                      }}
                    >
                      {tr('common.next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <BlockedUsersModal open={blockedOpen} onClose={() => setBlockedOpen(false)} uid={uid} isForest={isForest} t={t} tr={tr} />
      <DeletedItemsModal open={deletedOpen} onClose={() => setDeletedOpen(false)} deletedItems={deletedItems} isForest={isForest} t={t} tr={tr} />

      {/* Decline Confirmation Modal */}
      <ConfirmModal
        open={!!pendingDeclineId}
        onClose={() => setPendingDeclineId(null)}
        onConfirm={() => { handleDecline(pendingDeclineId); setPendingDeclineId(null); }}
        title={tr('sharedLists.decline')}
        message={tr('sharedLists.declineConfirm')}
        confirmLabel={tr('sharedLists.decline')}
        cancelLabel={tr('common.cancel')}
        destructive={true}
      />
    </div>
  );
}
